import React, { useState, useEffect } from 'react';
import { X, ArrowRight, CheckCircle, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { checkUser } from '../services/api';
import api from '../services/api';

const LoginModal = ({ isOpen, onClose, onLoginSuccess, onSignUp }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setEmail('');
            setPassword('');
            setError('');
            setIsLoading(false);
            setIsSuccess(false);
            setShowForgotPassword(false);
            setResetEmailSent(false);
        }
    }, [isOpen]);

    // Email validation
    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleForgotPassword = async () => {
        if (!isValidEmail(email)) {
            setError('Please enter your email address first');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await api.post('/request-password-reset', { email });
            setResetEmailSent(true);
        } catch (err) {
            if (err.response?.status === 404) {
                setError('No account found with this email');
            } else {
                setError('Failed to send reset email. Try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isValidEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }

        if (!password) {
            setError('Please enter your password');
            return;
        }

        setError('');
        setIsLoading(true);

        try {
            const response = await checkUser(email, password);

            if (response) {
                setIsSuccess(true);
                setTimeout(() => {
                    onLoginSuccess(response);
                    onClose();
                }, 1000);
            } else {
                setError('Invalid email or password');
                setIsLoading(false);
            }
        } catch (err) {
            console.error('Login error:', err);
            if (err.response && err.response.status === 401) {
                setError('Invalid email or password');
            } else if (err.response && err.response.status === 404) {
                setError('User not found. Please sign up first.');
            } else {
                setError('Connection issue. Please try again.');
            }
            setIsLoading(false);
        }
    };

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    onClick={onClose}
                />

                {/* Modal */}
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors z-10"
                    >
                        <X size={24} />
                    </button>

                    {/* Success State */}
                    {isSuccess ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="p-12 text-center"
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2, type: 'spring' }}
                            >
                                <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
                            </motion.div>
                            <h2 className="text-2xl font-bold text-slate-800">Welcome back!</h2>
                        </motion.div>
                    ) : showForgotPassword ? (
                        /* Forgot Password View */
                        <div className="p-8">
                            <button
                                onClick={() => {
                                    setShowForgotPassword(false);
                                    setResetEmailSent(false);
                                    setError('');
                                }}
                                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4"
                            >
                                <ArrowLeft size={16} /> Back to login
                            </button>

                            {resetEmailSent ? (
                                <div className="text-center py-8">
                                    <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Check your email</h2>
                                    <p className="text-slate-500">We've sent a password reset link to <strong>{email}</strong></p>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Reset password</h2>
                                    <p className="text-slate-500 mb-6">Enter your email and we'll send you a reset link.</p>

                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setError('');
                                        }}
                                        placeholder="your.email@example.com"
                                        className="w-full px-4 py-4 rounded-2xl border-2 border-slate-200 focus:border-amber-500 text-lg transition-colors focus:outline-none mb-4"
                                        autoFocus
                                    />

                                    {error && (
                                        <p className="text-red-500 text-sm mb-4">{error}</p>
                                    )}

                                    <button
                                        onClick={handleForgotPassword}
                                        disabled={!email || isLoading}
                                        className="w-full py-4 rounded-2xl font-bold text-lg bg-amber-500 text-white hover:bg-amber-600 disabled:bg-slate-200 disabled:text-slate-400 transition-all"
                                    >
                                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </>
                            )}
                        </div>
                    ) : (
                        /* Login Form */
                        <div className="p-8">
                            <h2 className="text-3xl font-bold text-slate-800 mb-2">Welcome back</h2>
                            <p className="text-slate-500 mb-6">Sign in to continue with Saige</p>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                    placeholder="your.email@example.com"
                                    className={`w-full px-4 py-4 rounded-2xl border-2 text-lg transition-colors focus:outline-none ${error ? 'border-red-300' : 'border-slate-200 focus:border-amber-500'}`}
                                    disabled={isLoading}
                                    autoFocus
                                />

                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                    placeholder="Password"
                                    className={`w-full px-4 py-4 rounded-2xl border-2 text-lg transition-colors focus:outline-none ${error ? 'border-red-300' : 'border-slate-200 focus:border-amber-500'}`}
                                    disabled={isLoading}
                                />

                                {error && (
                                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-red-500 text-sm">
                                        {error}
                                    </motion.p>
                                )}

                                <button
                                    type="submit"
                                    disabled={!email || !password || isLoading}
                                    className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${!email || !password || isLoading ? 'bg-slate-200 text-slate-400' : 'bg-slate-900 text-white hover:bg-black'}`}
                                >
                                    {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>Continue <ArrowRight size={20} /></>}
                                </button>

                                <div className="text-center">
                                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-slate-500 hover:text-amber-500">
                                        Forgot password?
                                    </button>
                                </div>
                            </form>

                            <div className="mt-8 text-center">
                                <p className="text-sm text-slate-500">
                                    Don't have an account?{' '}
                                    <button onClick={() => { onClose(); onSignUp?.(); }} className="text-amber-500 font-semibold hover:underline">
                                        Sign up
                                    </button>
                                </p>
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default LoginModal;
